import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import {
  getTicketRules,
  createTicketRule,
  updateTicketRule,
  deleteTicketRule,
  reorderTicketRules,
  getTicketProjects,
  getTicketTags,
  getUsers,
  errorMessage,
} from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import '../components/kernelSwitch.css';
import { PageHeading, Pill, NtIcon } from '../components/NotionUI';
import Select from '../components/Select';
import { TagChip } from '../components/TicketTags';

const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const emptyForm = {
  name: '', event: 'ticket_created', project_id: '', is_active: true,
  subject_contains: '', text_contains: '', customer_email_contains: '', priority: [],
  assign_mode: 'none', assign_user_id: '', assign_round_robin: [],
  set_priority: '', add_tag_ids: [], set_due_hours: '', internal_note: '', stop: false,
};

const joinList = (v) => (Array.isArray(v) ? v.join(', ') : '');

function toForm(rule) {
  const c = rule.conditions || {};
  const a = rule.actions || {};
  return {
    name: rule.name, event: rule.event, project_id: rule.project_id ? String(rule.project_id) : '', is_active: rule.is_active,
    subject_contains: joinList(c.subject_contains), text_contains: joinList(c.text_contains), customer_email_contains: joinList(c.customer_email_contains), priority: c.priority || [],
    assign_mode: a.assign_user_id ? 'user' : a.assign_round_robin ? 'round' : 'none',
    assign_user_id: a.assign_user_id ? String(a.assign_user_id) : '', assign_round_robin: a.assign_round_robin || [],
    set_priority: a.set_priority || '', add_tag_ids: a.add_tag_ids || [], set_due_hours: a.set_due_hours ? String(a.set_due_hours) : '',
    internal_note: a.internal_note || '', stop: !!a.stop,
  };
}

function toPayload(f) {
  const conditions = {
    subject_contains: f.subject_contains, text_contains: f.text_contains, customer_email_contains: f.customer_email_contains, priority: f.priority,
  };
  const actions = { set_priority: f.set_priority || undefined, add_tag_ids: f.add_tag_ids, set_due_hours: f.set_due_hours ? Number(f.set_due_hours) : undefined, internal_note: f.internal_note, stop: f.stop };
  if (f.assign_mode === 'user' && f.assign_user_id) actions.assign_user_id = Number(f.assign_user_id);
  if (f.assign_mode === 'round') actions.assign_round_robin = f.assign_round_robin;
  return { name: f.name.trim(), event: f.event, project_id: f.project_id ? Number(f.project_id) : null, is_active: f.is_active, conditions, actions };
}

function Rules({ user, onLogout }) {
  const { t } = useLanguage();
  const [rules, setRules] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tags, setTags] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p, g] = await Promise.all([getTicketRules(), getTicketProjects(), getTicketTags()]);
      setRules(r.rules || []);
      setProjects(p.projects || []);
      setTags(g.tags || []);
    } catch (err) {
      setError(errorMessage(err, t('rules.loadError')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
    getUsers().then((d) => setAgents((d.users || []).filter((u) => Number(u.status) === 1))).catch(() => {});
  }, [load]);

  const agentName = (id) => {
    const u = agents.find((x) => Number(x.id) === Number(id));
    return u ? `${u.name} ${u.surname || ''}`.trim() : `#${id}`;
  };
  const tagOf = (id) => tags.find((x) => x.id === id);

  const describe = (rule) => {
    const c = rule.conditions || {};
    const a = rule.actions || {};
    const ifs = [];
    if (c.subject_contains?.length) ifs.push(t('rules.dSubject', { words: c.subject_contains.join(', ') }));
    if (c.text_contains?.length) ifs.push(t('rules.dText', { words: c.text_contains.join(', ') }));
    if (c.customer_email_contains?.length) ifs.push(t('rules.dEmail', { words: c.customer_email_contains.join(', ') }));
    if (c.priority?.length) ifs.push(t('rules.dPriority', { values: c.priority.map((p) => t(`tickets.priority_${p}`)).join(', ') }));
    const thens = [];
    if (a.assign_user_id) thens.push(t('rules.dAssign', { name: agentName(a.assign_user_id) }));
    if (a.assign_round_robin?.length) thens.push(t('rules.dRound', { names: a.assign_round_robin.map(agentName).join(', ') }));
    if (a.set_priority) thens.push(t('rules.dSetPriority', { value: t(`tickets.priority_${a.set_priority}`) }));
    if (a.add_tag_ids?.length) thens.push(t('rules.dTags', { names: a.add_tag_ids.map((id) => tagOf(id)?.name || `#${id}`).join(', ') }));
    if (a.set_due_hours) thens.push(t('rules.dDue', { hours: a.set_due_hours }));
    if (a.internal_note) thens.push(t('rules.dNote'));
    return { ifs: ifs.length ? ifs : [t('rules.dAlways')], thens };
  };

  const startCreate = () => { setEditId(null); setForm(emptyForm); setShowForm(true); setError(''); };
  const startEdit = (rule) => { setEditId(rule.id); setForm(toForm(rule)); setShowForm(true); setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = toPayload(form);
      if (editId) await updateTicketRule(editId, payload);
      else await createTicketRule(payload);
      setShowForm(false);
      await load();
    } catch (err) {
      setError(errorMessage(err, t('rules.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (rule) => {
    try {
      await updateTicketRule(rule.id, { is_active: !rule.is_active });
      await load();
    } catch (err) {
      setError(errorMessage(err, t('rules.saveError')));
    }
  };

  const remove = async (rule) => {
    if (!window.confirm(t('rules.confirmDelete', { name: rule.name }))) return;
    try {
      await deleteTicketRule(rule.id);
      await load();
    } catch (err) {
      setError(errorMessage(err, t('rules.deleteError')));
    }
  };

  const move = async (index, delta) => {
    const next = [...rules];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setRules(next);
    try {
      await reorderTicketRules(next.map((r) => r.id));
    } catch (err) {
      setError(errorMessage(err, t('rules.saveError')));
      load();
    }
  };

  const toggleIn = (key, value) => setForm((f) => ({ ...f, [key]: f[key].includes(value) ? f[key].filter((x) => x !== value) : [...f[key], value] }));

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page">
        <PageHeading icon="sliders" title={t('rules.title')} subtitle={t('rules.subtitle')} />
        {error && <div className="error-message">{error}</div>}

        {!showForm && (
          <div className="tp-toolbar">
            <span className="nt-muted">{loading ? '' : t('rules.count', { count: rules.length })}</span>
            <button className="btn" type="button" onClick={startCreate}>{t('rules.new')}</button>
          </div>
        )}

        {showForm && (
          <form className="tp-form" onSubmit={handleSubmit}>
            <section className="tp-section">
              <h3 className="tp-section-title">{t('rules.secGeneral')}</h3>
              <div className="tp-fields">
                <div className="form-group tp-full">
                  <label>{t('rules.fName')}</label>
                  <input value={form.name} maxLength={120} required onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('rules.fNamePlaceholder')} />
                </div>
                <div className="form-group">
                  <label>{t('rules.fEvent')}</label>
                  <Select value={form.event} onChange={(e) => setForm({ ...form, event: e.target.value })}>
                    <option value="ticket_created">{t('rules.event_ticket_created')}</option>
                    <option value="customer_reply">{t('rules.event_customer_reply')}</option>
                  </Select>
                </div>
                <div className="form-group">
                  <label>{t('rules.fQueue')}</label>
                  <Select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}>
                    <option value="">{t('rules.anyQueue')}</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </Select>
                </div>
              </div>
            </section>

            <section className="tp-section">
              <h3 className="tp-section-title">{t('rules.secIf')}</h3>
              <p className="tp-hint">{t('rules.ifHint')}</p>
              <div className="tp-fields">
                <div className="form-group tp-full">
                  <label>{t('rules.fSubject')}</label>
                  <input value={form.subject_contains} onChange={(e) => setForm({ ...form, subject_contains: e.target.value })} placeholder={t('rules.wordsPlaceholder')} />
                </div>
                <div className="form-group tp-full">
                  <label>{t('rules.fText')}</label>
                  <input value={form.text_contains} onChange={(e) => setForm({ ...form, text_contains: e.target.value })} placeholder={t('rules.wordsPlaceholder')} />
                </div>
                <div className="form-group tp-full">
                  <label>{t('rules.fEmail')}</label>
                  <input value={form.customer_email_contains} onChange={(e) => setForm({ ...form, customer_email_contains: e.target.value })} placeholder="@azienda.it" />
                </div>
                <div className="form-group tp-full">
                  <label>{t('rules.fPriority')}</label>
                  <div className="rl-checks">
                    {PRIORITIES.map((p) => (
                      <label key={p} className="rp-check"><input type="checkbox" checked={form.priority.includes(p)} onChange={() => toggleIn('priority', p)} /> {t(`tickets.priority_${p}`)}</label>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="tp-section">
              <h3 className="tp-section-title">{t('rules.secThen')}</h3>
              <div className="tp-fields">
                <div className="form-group tp-full">
                  <label>{t('rules.fAssign')}</label>
                  <Select value={form.assign_mode} onChange={(e) => setForm({ ...form, assign_mode: e.target.value })}>
                    <option value="none">{t('rules.assignNone')}</option>
                    <option value="user">{t('rules.assignUser')}</option>
                    <option value="round">{t('rules.assignRound')}</option>
                  </Select>
                </div>
                {form.assign_mode === 'user' && (
                  <div className="form-group tp-full">
                    <Select searchable value={form.assign_user_id} onChange={(e) => setForm({ ...form, assign_user_id: e.target.value })}>
                      <option value="">{t('rules.pickPerson')}</option>
                      {agents.map((u) => <option key={u.id} value={u.id}>{`${u.name} ${u.surname || ''}`.trim()}</option>)}
                    </Select>
                  </div>
                )}
                {form.assign_mode === 'round' && (
                  <div className="form-group tp-full">
                    <p className="tp-hint">{t('rules.roundHint')}</p>
                    <div className="rl-checks">
                      {agents.map((u) => (
                        <label key={u.id} className="rp-check"><input type="checkbox" checked={form.assign_round_robin.includes(Number(u.id))} onChange={() => toggleIn('assign_round_robin', Number(u.id))} /> {`${u.name} ${u.surname || ''}`.trim()}</label>
                      ))}
                      {agents.length === 0 && <span className="nt-muted">{t('rules.noAgents')}</span>}
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <label>{t('rules.fSetPriority')}</label>
                  <Select value={form.set_priority} onChange={(e) => setForm({ ...form, set_priority: e.target.value })}>
                    <option value="">{t('rules.leaveAsIs')}</option>
                    {PRIORITIES.map((p) => <option key={p} value={p}>{t(`tickets.priority_${p}`)}</option>)}
                  </Select>
                </div>
                <div className="form-group">
                  <label>{t('rules.fDue')}</label>
                  <input type="number" min="1" max="8760" value={form.set_due_hours} onChange={(e) => setForm({ ...form, set_due_hours: e.target.value })} placeholder={t('rules.hoursPlaceholder')} />
                </div>
                <div className="form-group tp-full">
                  <label>{t('rules.fTags')}</label>
                  <div className="rl-checks">
                    {tags.map((tag) => (
                      <label key={tag.id} className="rp-check"><input type="checkbox" checked={form.add_tag_ids.includes(tag.id)} onChange={() => toggleIn('add_tag_ids', tag.id)} /> <TagChip tag={tag} /></label>
                    ))}
                    {tags.length === 0 && <span className="nt-muted">{t('rules.noTags')}</span>}
                  </div>
                </div>
                <div className="form-group tp-full">
                  <label>{t('rules.fNote')}</label>
                  <textarea rows={2} value={form.internal_note} onChange={(e) => setForm({ ...form, internal_note: e.target.value })} placeholder={t('rules.notePlaceholder')} />
                </div>
                <div className="form-group tp-full">
                  <label className="ksw-row"><span className="ksw"><input type="checkbox" checked={form.stop} onChange={(e) => setForm({ ...form, stop: e.target.checked })} /><span className="ksw-slider" /></span><span>{t('rules.fStop')}</span></label>
                  <label className="ksw-row"><span className="ksw"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /><span className="ksw-slider" /></span><span>{t('rules.fActive')}</span></label>
                </div>
              </div>
            </section>

            <div className="tp-savebar">
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>{t('common.cancel')}</button>
              <button type="submit" className="btn" disabled={saving}>{saving ? t('common.saving') : t('common.save')}</button>
            </div>
          </form>
        )}

        {!showForm && (loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : rules.length === 0 ? (
          <div className="au-empty">
            <span className="au-empty-icon" aria-hidden="true"><NtIcon name="sliders" size={26} /></span>
            <p>{t('rules.empty')}</p>
            <button className="btn" type="button" onClick={startCreate}>{t('rules.new')}</button>
          </div>
        ) : (
          <div className="cn-list">
            <p className="tp-hint">{t('rules.orderHint')}</p>
            {rules.map((rule, i) => {
              const d = describe(rule);
              const queue = rule.project_id ? projects.find((p) => p.id === rule.project_id)?.name : null;
              return (
                <div key={rule.id} className={'cn-card' + (rule.is_active ? '' : ' rl-off')}>
                  <div className="cn-head">
                    <span className="rl-pos">{i + 1}</span>
                    <h3 className="cn-title">{rule.name}</h3>
                    <Pill>{t(`rules.event_${rule.event}`)}</Pill>
                    {queue ? <Pill>{queue}</Pill> : null}
                    {!rule.is_active && <span className="nt-muted">{t('rules.inactive')}</span>}
                    <span className="tp-spacer" />
                    <button className="tp-icon" type="button" onClick={() => move(i, -1)} disabled={i === 0} title={t('rules.moveUp')} aria-label={t('rules.moveUp')}>↑</button>
                    <button className="tp-icon" type="button" onClick={() => move(i, 1)} disabled={i === rules.length - 1} title={t('rules.moveDown')} aria-label={t('rules.moveDown')}>↓</button>
                    <button className="btn btn-secondary btn-sm" type="button" onClick={() => toggle(rule)}>{rule.is_active ? t('rules.disable') : t('rules.enable')}</button>
                    <button className="tp-icon" type="button" onClick={() => startEdit(rule)} title={t('common.edit')} aria-label={t('common.edit')}><NtIcon name="edit" size={16} /></button>
                    <button className="tp-icon tp-icon-danger" type="button" onClick={() => remove(rule)} title={t('common.delete')} aria-label={t('common.delete')}><NtIcon name="trash" size={16} /></button>
                  </div>
                  <p className="cn-body"><strong>{t('rules.ifLabel')}</strong> {d.ifs.join(' · ')}<br /><strong>{t('rules.thenLabel')}</strong> {d.thens.join(' · ')}</p>
                  <p className="nt-muted rl-meta">{t('rules.runs', { count: rule.runs })}</p>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </Layout>
  );
}

export default Rules;
